import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  readingOrder,
  type EditableLayout,
} from "@/components/CaseManagement/caseLayout";

/** The section "card" idiom shared by the case view, task viewers and the
 * layout editor — one place for the visual language. */
export const LayoutSectionCard = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "bg-card border-0 shadow-[0_0_0_1px_var(--border)] rounded-lg",
      className
    )}
  >
    {children}
  </div>
);

/** Renders cells in a case-layout arrangement (sections of full rows on a
 * 12-column grid), delegating cell content to the caller. Used by task
 * viewers to inherit the case view's arrangement for linked properties. */
export const LayoutArranged = ({
  layout,
  heightOf,
  renderCell,
  untitledInCard = false,
}: {
  layout: EditableLayout;
  heightOf: (fieldId: string) => number;
  renderCell: (fieldId: string) => ReactNode;
  untitledInCard?: boolean;
}) => (
  <div className="space-y-5">
    {layout.sections.map((section, sectionIndex) => {
      const grid = (
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
          {readingOrder(section.cells).map((cell) => (
            <div
              key={cell.field_id}
              className="min-w-0 sm:[grid-area:var(--cell-area)]"
              style={
                {
                  "--cell-area": `${cell.y + 1} / ${cell.x + 1} / span ${heightOf(
                    cell.field_id
                  )} / span ${cell.w}`,
                } as CSSProperties
              }
            >
              {renderCell(cell.field_id)}
            </div>
          ))}
        </div>
      );
      if (section.title === null) {
        return untitledInCard ? (
          <LayoutSectionCard key={`untitled-${sectionIndex}`} className="p-4">
            {grid}
          </LayoutSectionCard>
        ) : (
          <div key={`untitled-${sectionIndex}`}>{grid}</div>
        );
      }
      return (
        <div key={section.title} className="space-y-2">
          <div className="min-w-0 text-sm font-medium text-primary">
            {section.title}
          </div>
          <LayoutSectionCard className="p-4">{grid}</LayoutSectionCard>
        </div>
      );
    })}
  </div>
);
